import { Award, StorefrontImage, UrgencyMessage, Wine } from "../../types";
import { Box, Heading, Section, Stack, Text } from "@vinomofo/components";

import { InlineAddToCart } from "../inline-add-to-cart";
import { MixedGrid } from "./";
import { OfferHeroMixed } from "../offer-hero";
import { OfferSellingPoints } from "../offer-hero/OfferSellingPoints";
import { VideoBlock } from "../video-block";
import { VinoDataOffer } from "../../types/vino-data-types";
import { WineNotesMixed } from "../wine-notes";
import { richTextParserRenderer } from "../../utils/rich-text-renderer";
import styled from "styled-components";
import useAuth from "../../hooks/use-auth";
import { getExternalUserID } from "../../../api/src/utils/auth";
import RelatedProductItems from "../related-offers/RelatedProductItems";
import { isProductionAU } from "../../utils/environment-helpers";
import { RelatedSimilarItems } from "../related-offers/RelatedSimilarItems";

const StyledBox = styled(Box)`
  margin: 0.5em auto 0;
  h2 {
    margin-top: 1em;
  }
  h3,
  h4 {
    margin-top: 0.8em;
  }
  p {
    margin-top: 0.8em;
  }
`;

const Description = styled(Text)`
  * + * {
    margin-top: 1em;
  }
  hr {
    border-top: 1px solid ${({ theme }) => theme.colors.mofoGreen};
    margin-top: 1.5em;
  }
  blockquote + blockquote {
    margin-top: 0;
  }
`;

export const WideHeroImageContainer = styled(Box)`
  background-color: transparent;
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;

  @media (min-width: 2201px) {
    background-size: contain;
  }
`;

export type OfferDetailsMixedProps = {
  productId: string;
  awards: Array<Award>;
  endsAt?: Date;
  description: any;
  videoUrl?: string;
  videoCaption?: string;
  discountPercentage: number;
  discountDollarValue: number;
  discountDisplay: string;
  price: number;
  rrp?: number;
  unitRRP?: number;
  packRRP?: number;
  showCountdown: boolean;
  showPriceDrop: boolean;
  priceDescription?: string;
  shortDescription: string;
  region: string;
  style: string;
  volume: string;
  wines: Array<Wine>;
  roundelsImage?: StorefrontImage;
  heroImage: StorefrontImage;
  wideHeroImage: StorefrontImage;
  largeHeroImage: StorefrontImage;
  showDiscountBadge: boolean;
  showAwardBadges: boolean;
  urgencyMessage?: UrgencyMessage;
  isSoldOut?: boolean;
  backgroundColor?: string;
  name: string;
  bottomDescription: any;
};

export const OfferDetailsMixed = ({
  details,
  offer,
}: {
  details: OfferDetailsMixedProps;
  offer: VinoDataOffer;
}): JSX.Element => {
  const {
    description,
    bottomDescription,
    videoUrl,
    videoCaption,
    wines,
    isSoldOut,
    productId,
    heroImage,
    wideHeroImage,
  } = details;
  const { user } = useAuth();
  const userId = getExternalUserID(user);
  return (
    <>
      {heroImage && wideHeroImage && (
        <WideHeroImageContainer
          p={0}
          display={["none", "none", "block"]}
          style={{
            backgroundImage: `url(${wideHeroImage?.images?.fallback?.src})`,
          }}
        >
          <OfferHeroMixed details={details} offer={offer} />
        </WideHeroImageContainer>
      )}

      <Section
        p="0"
        backgroundColor="transparent"
        display={["block", "block", !wideHeroImage ? "block" : "none"]}
      >
        <OfferHeroMixed details={details} offer={offer} />
      </Section>

      <OfferSellingPoints
        isAvailable={!isSoldOut}
        marginBottom={0}
        minHeight={0}
        isSpirit={offer?.isSpirit}
        withBG
      />
      {isSoldOut &&
        (isProductionAU ? (
          <RelatedProductItems
            indexName={process.env.GATSBY_ALGOLIA_OFFERS_INDEX}
            offerIds={[offer?.id]}
            heading="Don't worry, try one of these"
            subheading=""
            columns={4}
            limit={4}
            userId={userId}
            hidden={offer?.hidden}
            category={offer?.category}
            isSpirit={offer?.isSpirit}
          />
        ) : (
          <RelatedSimilarItems
            offerId={offer?.id}
            skuCodes={offer?.skuCode ? [offer?.skuCode] : []}
            userId={userId}
            heading="Don't worry, try one of these"
            limit={4}
          />
        ))}
      <Section py={["20px", "20px"]} backgroundColor="white">
        <Box
          display="block"
          flexDirection={["column", "column", "row"]}
          justifyContent="space-between"
        >
          <Box flexBasis={["100%", "100%", "100%"]} mb={["30px", "50px", 0]}>
            <Stack space={4}>
              <Heading as="h2" variant="xl">
                Why do we love this{" "}
                {offer?.isSpirit && offer?.category?.toLowerCase() !== "various"
                  ? offer?.category?.toLowerCase()
                  : "case"}
                ?
              </Heading>
              <Description fontSize={["16px", "18px", "20px"]} as="div">
                {richTextParserRenderer(description)}
              </Description>
              {videoUrl && <VideoBlock url={videoUrl} caption={videoCaption} />}
            </Stack>
          </Box>
          <Box
            flexBasis={["100%", "100%", "100%"]}
            maxWidth={["100%", "100%", "100%"]}
            justifyContent="center"
            display="flex"
            flexDirection="column"
            pt={["15px", "35px", "70px"]}
          >
            <Heading as="h4" variant="xs">
              Included in this case:
            </Heading>
            <MixedGrid wines={wines} />
            <InlineAddToCart
              productId={productId}
              offer={offer}
              isSoldOut={isSoldOut}
              marginTop={["10px", "0px", "0"]}
              paddingTop={["20px", "40px", "60px"]}
            />
          </Box>
        </Box>
      </Section>

      {wines.map((wine, i) => (
        <WineNotesMixed
          key={i}
          wine={wine}
          backgroundColor={i % 2 ? "white" : "almostWhite"}
          isSpirit={offer.isSpirit}
        />
      ))}
      {bottomDescription ? (
        <>
          <InlineAddToCart
            productId={productId}
            offer={offer}
            isSoldOut={isSoldOut}
            marginTop={["10px", "0px", "-50px"]}
            paddingTop={["20px", "40px", "50px"]}
          />
          <Section py={["50px", "50px"]} backgroundColor="white">
            <StyledBox>{richTextParserRenderer(bottomDescription)}</StyledBox>
          </Section>
        </>
      ) : null}
      <InlineAddToCart
        productId={productId}
        offer={offer}
        isSoldOut={isSoldOut}
      />
      {!isSoldOut &&
        (isProductionAU ? (
          <RelatedProductItems
            indexName={process.env.GATSBY_ALGOLIA_OFFERS_INDEX}
            offerIds={[offer?.id]}
            heading="You might also like…"
            subheading=""
            columns={4}
            limit={4}
            userId={userId}
            hidden={offer?.hidden}
            category={offer?.category}
            isSpirit={offer?.isSpirit}
          />
        ) : (
          <RelatedSimilarItems
            offerId={offer?.id}
            skuCodes={offer?.skuCode ? [offer?.skuCode] : []}
            userId={userId}
            heading="You might also like…"
            limit={4}
          />
        ))}
    </>
  );
};
